// React
import { useMemo } from 'react'

// Types
import { LeadDetail } from 'store/types/lead'
import { LeadDataConfig } from '../components/MergeConfirmationModal/types'

// Hooks
import { useReusableTab } from 'hooks/use-reusable-lead-tab'

// UI / Components
import { Badge, Flex, Icon, Text } from 'components'
import { StyledLinkFlex } from 'modules/shared/styles'

// Constants
import { LEAD_DUPLICATE_TAB } from 'utils/constants'

// Utils
import { colors } from 'utils/colors'
import { formatDatetime } from 'utils/datetime'
import { getLeadRoute } from 'utils/url'

// Local helpers
import { getLeadOwnerName, getLeadStatusChipGroup } from '../components/helpers'
import LeadStatusChipV2 from 'modules/Leads/shared/LeadStatus/LeadStatusV2'

export default function useLeadDataConfig() {
  const { openInReusableTab } = useReusableTab({ tabName: LEAD_DUPLICATE_TAB })

  const columns: LeadDataConfig<Partial<LeadDetail>>[] = useMemo(
    () => [
      {
        title: 'Lead',
        key: 'leadName',
        accessor: (row: Partial<LeadDetail>) => row,
        render: (row: Partial<LeadDetail>) => (
          <>
            {row?.leadName ? (
              <Flex gap="4px">
                <StyledLinkFlex
                  style={{ textDecoration: 'none', color: colors?.blue1 }}
                  to={row.leadId ? getLeadRoute({ leadId: row.leadId }) : ''}
                  onClick={(e) => {
                    e.preventDefault()
                    row.leadId && openInReusableTab(getLeadRoute({ leadId: row.leadId }))
                  }}
                >
                  <Text ellipsis maxWidth={'224px'} textDecoration="none">
                    {row?.leadName}
                  </Text>
                </StyledLinkFlex>
                <Icon marginLeft={'5px'} color={colors?.blue1} icon={['far', 'fa-arrow-up-right-from-square']} />
              </Flex>
            ) : (
              ''
            )}
          </>
        ),
      },
      {
        title: 'Lead ID',
        key: 'leadId',
        accessor: (row: Partial<LeadDetail>) => row.leadId,
      },
      {
        title: 'Lead type',
        key: 'leadTypeName',
        accessor: (row: Partial<LeadDetail>) => row.leadTypeName,
      },
      {
        title: 'Lead status',
        key: 'leadStatusName',
        accessor: (row: Partial<LeadDetail>) => row,
        render: (row: Partial<LeadDetail>) => {
          const chipGroup = getLeadStatusChipGroup(row?.leadStatusName)
          if (chipGroup) {
            return <LeadStatusChipV2 statusGroup={chipGroup} />
          }
          return <Badge text={row.leadStatusName} color="disabled" textColor="black" />
        },
      },
      {
        title: 'Lead owner',
        key: 'owner',
        accessor: (row: Partial<LeadDetail>) => row,
        render: (row: Partial<LeadDetail>) => (
          <Text fontSize={13} color={getLeadOwnerName(row) == 'unassigned_lbl' ? 'red1' : 'gray5'}>
            {getLeadOwnerName(row)}
          </Text>
        ),
      },
      {
        title: 'Lead in date',
        key: 'leadInDate',
        accessor: (row: Partial<LeadDetail>) => formatDatetime(row.leadInDate, 'PPPp'),
      },
      {
        title: 'Last activity',
        key: 'lastActivity',
        accessor: (row: Partial<LeadDetail>) => formatDatetime(row.lastActivity?.lastActivityTs, 'PPPp'),
      },
    ],
    [],
  )

  return {
    columns,
  }
}
